/**
 * A training agent session's transcript (TRA-1327), built from its events.
 *
 * The api numbers every event, so the page replays them in `seq` order:
 * what people did (`user`, `approval_resolved`, `status`) and what the
 * agent did (`init`, `text`, `tool_use`, `tool_result`, `approval`, `turn`,
 * `error`). A tool result joins the call it answers and a decision joins
 * the request it settles, so each renders as one row. A launch request
 * (TRA-1326) moves from asked to approved to its outcome the same way.
 * Payloads are JSON, so every field is read with a type check.
 */

import {
  TrainingAgentSessionViewStatus,
  type TrainingAgentEventView,
  type TrainingAgentEventViewType,
} from '@/api/generated/model'

export type SessionStatus = TrainingAgentSessionViewStatus

const LIVE_STATUSES: readonly SessionStatus[] = ['starting', 'idle', 'working', 'approval']

export interface ToolResult {
  text: string
  omitted: number
  isError: boolean
}

export interface ToolCall {
  id: string
  name: string
  input: unknown
  result: ToolResult | null
  /** A person has yet to approve or decline this call. */
  awaitingApproval: boolean
}

export interface Decision {
  approved: boolean
  by: string
}

export interface UserTurn {
  kind: 'user'
  seq: number
  text: string
  by: string
}

export interface AgentText {
  kind: 'agent'
  seq: number
  text: string
  omitted: number
}

/** Consecutive tool calls, shown as one block. */
export interface ToolGroup {
  kind: 'tools'
  seq: number
  calls: ToolCall[]
}

export interface ApprovalRequest {
  kind: 'approval'
  seq: number
  id: string
  tool: string
  input: unknown
  decision: Decision | null
}

export interface Note {
  kind: 'note'
  seq: number
  label: string
  text: string
  isError: boolean
}

/** One field the launch changes in the preset its set's recipe derives (TRA-1345). */
export interface PresetChange {
  field: string
  derived: number
  chosen: number
}

/** What the launch would run, as the api read it when the agent asked. */
export interface LaunchPreview {
  kind: string
  trainsOn: string
  digests: Record<string, string>
  preset: string
  trainerRef: string
  epochs: number | null
  changes: PresetChange[]
  compute: string
  deploy: string
}

/** The agent's case for the run. */
export interface LaunchCase {
  summary: string
  checks: string[]
  risks: string[]
  expectedSteps: number | null
}

export type LaunchState =
  | { kind: 'pending' }
  /** Launching until `expiresAt`. Past it, the launch never reported back (TRA-1347). */
  | { kind: 'approved'; by: string; expiresAt: string }
  | { kind: 'launched'; by: string; runId: string }
  | { kind: 'declined'; by: string; reason: string }
  | { kind: 'failed'; by: string; error: string }

export interface LaunchRequest {
  kind: 'launch'
  seq: number
  id: string
  preview: LaunchPreview
  ask: LaunchCase
  state: LaunchState
}

export type TranscriptItem =
  | UserTurn
  | AgentText
  | ToolGroup
  | ApprovalRequest
  | LaunchRequest
  | Note

export interface Transcript {
  items: TranscriptItem[]
  /** The status of the newest status event, or null before the first. */
  status: SessionStatus | null
  /** The team-data tools the agent started with, from its init event. */
  tools: string[]
}

type Payload = Readonly<Record<string, unknown>>

interface Builder {
  items: TranscriptItem[]
  calls: Map<string, ToolCall>
  approvals: Map<string, ApprovalRequest>
  launches: Map<string, LaunchRequest>
  status: SessionStatus | null
  tools: string[]
}

type Handler = (state: Builder, seq: number, payload: Payload) => void

function text(payload: Payload, key: string): string {
  const value = payload[key]
  return typeof value === 'string' ? value : ''
}

function count(payload: Payload, key: string): number {
  const value = payload[key]
  return typeof value === 'number' ? value : 0
}

function record(value: unknown): Payload {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Payload)
    : {}
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function presetChanges(value: unknown): PresetChange[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry: unknown) => {
    const change = record(entry)
    const derived = numberOrNull(change.derived)
    const chosen = numberOrNull(change.chosen)
    const field = text(change, 'field')
    return field !== '' && derived !== null && chosen !== null ? [{ field, derived, chosen }] : []
  })
}

function preview(payload: Payload): LaunchPreview {
  const digests = Object.entries(record(payload.digests)).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  )
  return {
    kind: text(payload, 'kind'),
    trainsOn: text(payload, 'trains_on'),
    digests: Object.fromEntries(digests),
    preset: text(payload, 'preset'),
    trainerRef: text(payload, 'trainer_ref'),
    epochs: numberOrNull(payload.epochs),
    changes: presetChanges(payload.changes),
    compute: text(payload, 'compute'),
    deploy: text(payload, 'deploy'),
  }
}

function isStatus(value: unknown): value is SessionStatus {
  return Object.values(TrainingAgentSessionViewStatus).some((status) => status === value)
}

export function isLive(status: SessionStatus): boolean {
  return LIVE_STATUSES.includes(status)
}

/**
 * How far the page has read a session's stream: the last seq, whether the
 * session is over, and the approved launches still waiting for their
 * outcome. A stop during a launch ends the session before the api records
 * the launch's outcome, so the page reads on until every approved launch
 * has one, or until its approval expires (TRA-1347): a launch that never
 * reported back gets no outcome once its session is over.
 */
export class StreamCursor {
  seq = -1
  private over = false
  /** When each waiting launch's approval expires, in epoch ms. */
  private readonly launching = new Map<string, number>()

  /** Take one event the page has not read yet. */
  read(event: TrainingAgentEventView): void {
    this.seq = event.seq
    const { id, status, expires_at: expiresAt } = event.payload
    if (event.type === 'status' && isStatus(status)) this.over ||= !isLive(status)
    if (typeof id !== 'string') return
    if (event.type === 'launch_approved') {
      this.launching.set(id, typeof expiresAt === 'string' ? Date.parse(expiresAt) : Number.NaN)
    }
    if (event.type === 'launch_resolved') this.launching.delete(id)
  }

  /** Nothing more will arrive: the session is over and no approved launch waits. */
  get settled(): boolean {
    const now = Date.now()
    return this.over && Array.from(this.launching.values()).every((expiresAt) => now >= expiresAt)
  }
}

/** `mcp__team-data__list_sft_sets` reads as `list_sft_sets`. */
export function toolLabel(name: string): string {
  return name.replace(/^mcp__.+?__/, '')
}

function addCall(state: Builder, seq: number, call: ToolCall): void {
  state.calls.set(call.id, call)
  const last = state.items[state.items.length - 1]
  if (last?.kind === 'tools') {
    last.calls.push(call)
    return
  }
  state.items.push({ kind: 'tools', seq, calls: [call] })
}

const startCall: Handler = (state, seq, payload) => {
  addCall(state, seq, {
    id: text(payload, 'id'),
    name: text(payload, 'name'),
    input: payload.input ?? null,
    result: null,
    awaitingApproval: false,
  })
}

const resolveCall: Handler = (state, seq, payload) => {
  const id = text(payload, 'tool_use_id')
  const result: ToolResult = {
    text: text(payload, 'text'),
    omitted: count(payload, 'omitted'),
    isError: payload.is_error === true,
  }
  const call = state.calls.get(id)
  if (call !== undefined) {
    call.result = result
    return
  }
  // A result whose call never arrived still shows, under its id.
  addCall(state, seq, { id, name: id, input: null, result, awaitingApproval: false })
}

const addApproval: Handler = (state, seq, payload) => {
  const request: ApprovalRequest = {
    kind: 'approval',
    seq,
    id: text(payload, 'id'),
    tool: text(payload, 'tool'),
    input: payload.input ?? null,
    decision: null,
  }
  state.approvals.set(request.id, request)
  state.items.push(request)
}

const resolveApproval: Handler = (state, _seq, payload) => {
  const request = state.approvals.get(text(payload, 'id'))
  if (request === undefined) return
  request.decision = { approved: payload.decision === 'approve', by: text(payload, 'by') }
}

const applyStatus: Handler = (state, seq, payload) => {
  const status = payload.status
  if (!isStatus(status)) return
  state.status = status
  if (status !== 'ended' && status !== 'failed') return
  state.items.push({
    kind: 'note',
    seq,
    label: status === 'ended' ? 'Session ended' : 'Session failed',
    text: text(payload, 'message'),
    isError: status === 'failed',
  })
}

/** A turn that stopped on an error says so. A clean turn adds nothing. */
const noteTurn: Handler = (state, seq, payload) => {
  if (payload.is_error !== true) return
  state.items.push({
    kind: 'note',
    seq,
    label: 'Turn stopped',
    text: text(payload, 'subtype'),
    isError: true,
  })
}

const askLaunch: Handler = (state, seq, payload) => {
  const ask = record(payload.request)
  const request: LaunchRequest = {
    kind: 'launch',
    seq,
    id: text(payload, 'id'),
    preview: preview(record(payload.preview)),
    ask: {
      summary: text(ask, 'summary'),
      checks: strings(ask.checks),
      risks: strings(ask.risks),
      expectedSteps: numberOrNull(ask.expected_optimizer_steps),
    },
    state: { kind: 'pending' },
  }
  state.launches.set(request.id, request)
  state.items.push(request)
}

/** The newest approval stands: a person may decide again once one expires. */
const approveLaunch: Handler = (state, _seq, payload) => {
  const request = state.launches.get(text(payload, 'id'))
  if (request === undefined) return
  request.state = { kind: 'approved', by: text(payload, 'by'), expiresAt: text(payload, 'expires_at') }
}

const resolveLaunch: Handler = (state, _seq, payload) => {
  const request = state.launches.get(text(payload, 'id'))
  if (request === undefined) return
  const by = text(payload, 'by')
  if (payload.outcome === 'launched') {
    request.state = { kind: 'launched', by, runId: text(payload, 'run_id') }
  } else if (payload.outcome === 'declined') {
    request.state = { kind: 'declined', by, reason: text(payload, 'reason') }
  } else {
    request.state = { kind: 'failed', by, error: text(payload, 'error') }
  }
}

const HANDLERS: Record<TrainingAgentEventViewType, Handler> = {
  user: (state, seq, payload) => {
    state.items.push({ kind: 'user', seq, text: text(payload, 'text'), by: text(payload, 'by') })
  },
  text: (state, seq, payload) => {
    state.items.push({
      kind: 'agent',
      seq,
      text: text(payload, 'text'),
      omitted: count(payload, 'omitted'),
    })
  },
  tool_use: startCall,
  tool_result: resolveCall,
  approval: addApproval,
  approval_resolved: resolveApproval,
  status: applyStatus,
  launch_request: askLaunch,
  launch_approved: approveLaunch,
  launch_resolved: resolveLaunch,
  turn: noteTurn,
  error: (state, seq, payload) => {
    state.items.push({ kind: 'note', seq, label: 'Error', text: text(payload, 'text'), isError: true })
  },
  init: (state, _seq, payload) => {
    const tools = payload.tools
    state.tools = Array.isArray(tools)
      ? tools.filter((tool): tool is string => typeof tool === 'string')
      : []
  },
}

export function buildTranscript(events: readonly TrainingAgentEventView[]): Transcript {
  const state: Builder = {
    items: [],
    calls: new Map(),
    approvals: new Map(),
    launches: new Map(),
    status: null,
    tools: [],
  }
  for (const event of events) HANDLERS[event.type](state, event.seq, event.payload)
  for (const call of state.calls.values()) {
    call.awaitingApproval = state.approvals.get(call.id)?.decision === null
  }
  return { items: state.items, status: state.status, tools: state.tools }
}
